/**
 * WizardMobileShell — shared mobile wrapper for all wizard steps.
 * Renders header (Plans back-link, overflow menu), step progress dots,
 * title, scrollable content, and Back/Continue buttons.
 */

import { useState, type ReactNode } from 'react';

interface WizardMobileShellProps {
  step: number;
  totalSteps: number;
  title: string;
  children: ReactNode;
  onBack?: () => void;
  onContinue: () => void;
  /** Blocks Continue while the step holds an entry it must not advance past. */
  continueDisabled?: boolean;
  onSaveAndExit?: () => void;
  lastSaved?: string;
  planBadge?: string | number;
}

export function WizardMobileShell({ step, totalSteps, title, children, onBack, onContinue, continueDisabled, onSaveAndExit, lastSaved, planBadge }: Readonly<WizardMobileShellProps>) {
  const [menuOpen, setMenuOpen] = useState(false);

  return (
    <div className="wizard-mobile-shell">
      {/* Header */}
      <div className="wizard-mobile-header">
        <span className="wizard-mobile-header__brand">Glide-Path</span>
        {planBadge && <span className="wizard-mobile-header__badge">{planBadge}</span>}
        {lastSaved && <span className="wizard-mobile-saved">Saved</span>}
        <div className="wizard-mobile-header__menu-wrapper">
          <button
            type="button"
            className="wizard-mobile-header__menu"
            aria-label="More options"
            onClick={() => setMenuOpen(!menuOpen)}
          >
            ⋮
          </button>
          {menuOpen && (
            <>
              <div className="wizard-mobile-menu-backdrop" role="button" tabIndex={0} onClick={() => setMenuOpen(false)} onKeyDown={e => { if (e.key === 'Enter' || e.key === ' ') setMenuOpen(false); }} />
              <div className="wizard-mobile-menu">
                <button type="button" className="wizard-mobile-menu__item" onClick={() => { setMenuOpen(false); onSaveAndExit?.(); }}>
                  Save & Finish Later
                </button>
              </div>
            </>
          )}
        </div>
      </div>

      {/* Compact step indicator — dots unreadable at 24px on iPhone SE */}
      <div className="wizard-mobile-step-badge" aria-label={`Step ${step} of ${totalSteps}: ${title}`} aria-hidden={menuOpen}>
        <span className="wizard-mobile-step-badge__label">Step {step}/{totalSteps}</span>
      </div>
      <h2 className="wizard-mobile-title">{title}</h2>

      {/* Content */}
      <div className="wizard-mobile-content" aria-hidden={menuOpen}>
        {children}
      </div>

      {/* Actions */}
      <div className="wizard-mobile-actions">
        {onBack && (
          <button type="button" className="mobile-btn mobile-btn--secondary" onClick={onBack}>
            ← Back
          </button>
        )}
        <button type="button" className="mobile-btn mobile-btn--primary" disabled={continueDisabled} onClick={onContinue}>
          Continue
        </button>
      </div>
    </div>
  );
}
